// frontend/src/contexts/WebSocketContext.js
import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const WebSocketContext = createContext();

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const { currentUser, getToken } = useAuth();
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [connectionError, setConnectionError] = useState(null);
  const [currentRoom, setCurrentRoom] = useState({
    roomCode: null,
    gameId: null,
    seatPosition: null
  });

    const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const connectionTimeout = useRef(null);
  const eventListeners = useRef(new Map());

  // === Ref to hold the latest game state (for stateHash) ===
  const gameStateRef = useRef(null);

  // === Method for GameContext to update the ref ===
  const updateGameStateRef = useCallback((newState) => {
    gameStateRef.current = newState;
  }, []);

  // Initialize Socket.io connection
  const initializeSocket = useCallback(() => {
    if (!currentUser) {
      console.log('No current user, skipping socket initialization');
      setConnectionStatus('disconnected');
      return;
    }

    if (socket) {
      console.log('Cleaning up existing socket');
      socket.disconnect();
      setSocket(null);
      setIsConnected(false);
    }

    setConnectionStatus('connecting');
    setConnectionError(null);

    const API_BASE_URL = process.env.REACT_APP_API_URL || `http://${typeof window !== 'undefined' ? window.location.hostname : 'localhost'}:5001`;

    const token = getToken();
    if (!token) {
      console.error('No token available for socket connection');
      setConnectionStatus('error');
      setConnectionError('Authentication token not found');
      return;
    }

    const newSocket = io(API_BASE_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: maxReconnectAttempts,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 10000,
      auth: { token: `Bearer ${token}` },
      extraHeaders: { Authorization: `Bearer ${token}` }
    });

    newSocket.on('connect', () => {
      setIsConnected(true);
      setConnectionStatus('connected');
      setConnectionError(null);
      reconnectAttempts.current = 0;
    });

    newSocket.on('connect_error', (error) => {
      setIsConnected(false);
      setConnectionStatus('error');
      setConnectionError(error.message || 'Connection failed');
      setTimeout(() => {
        if (reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current++;
          setConnectionStatus('reconnecting');
          newSocket.connect();
        }
      }, 2000);
    });

    newSocket.on('disconnect', (reason) => {
      setIsConnected(false);
      // If this socket's session was invalidated (e.g. the server ended it because
      // a game window opened a new connection for the same user), do NOT reconnect.
      // Reconnecting here would kick the game window out and loop forever.
      if (newSocket.sessionInvalid) {
        setConnectionStatus('disconnected');
        return;
      }
      if (reason === 'io server disconnect' || reason === 'transport close') {
        setConnectionStatus('reconnecting');
        setTimeout(() => newSocket.connect(), 1000);
      } else {
        setConnectionStatus('disconnected');
      }
    });

    newSocket.on('reconnect', (attemptNumber) => {
      setIsConnected(true);
      setConnectionStatus('connected');
      setConnectionError(null);
      reconnectAttempts.current = 0;
    });

    newSocket.on('reconnect_attempt', () => setConnectionStatus('reconnecting'));

    newSocket.on('reconnect_error', (error) => {
      setConnectionStatus('error');
      setConnectionError(error.message || 'Reconnection failed');
    });

    newSocket.on('reconnect_failed', () => {
      setIsConnected(false);
      setConnectionStatus('error');
      setConnectionError('Failed to reconnect after maximum attempts');
    });

    newSocket.on('connected', (data) => {
      setIsConnected(true);
      setConnectionStatus('connected');
    });

    newSocket.on('error', (errorData) => {
      setConnectionError(errorData?.message || 'Socket error occurred');
    });

    newSocket.on('room-joined', (data) => {
      if (data && data.success && data.roomCode) {
        setCurrentRoom({ roomCode: data.roomCode, gameId: data.gameId, seatPosition: data.seatPosition });
      }
    });

    newSocket.on('session-ended', (data) => {
      newSocket.sessionInvalid = true;
      newSocket.io.reconnection(false);
      newSocket.disconnect();
      window.dispatchEvent(new CustomEvent('session-ended', { detail: data }));
    });

    newSocket.on('redirect-to-game', (data) => {
      window.dispatchEvent(new CustomEvent('redirect-to-game', { detail: data }));
    });

    newSocket.on('room-closed', () => {
      setCurrentRoom({ roomCode: null, gameId: null, seatPosition: null });
    });

    // 🚫 GAME CANCELLED: a game couldn't start because too many players
    // disconnected during seating. Redirect the remaining player back to the
    // lobby so they can join a fresh room instead of waiting on a ghost opponent.
    newSocket.on('game-cancelled', (data) => {
      console.log('🚫 game-cancelled received:', data);
      setCurrentRoom({ roomCode: null, gameId: null, seatPosition: null });
      const message = data?.message || 'The other player disconnected. Please join a new room.';
      try { sessionStorage.setItem('gameCancelledMessage', message); } catch (e) { /* ignore */ }
      window.dispatchEvent(new CustomEvent('game-cancelled', { detail: data }));
      const path = window.location.pathname;
      if (path.startsWith('/game/') || path.includes('/waiting/')) {
        window.location.href = '/multiplayer';
      }
    });

    // 🌐 Global safety-net: if the waiting-room component missed 'seating-phase-started'
    // (e.g. it was not yet mounted, or the socket room membership was reset), navigate
    // to the game table here so no player is left stuck on the waiting screen.
    newSocket.on('seating-phase-started', (data) => {
      if (data && data.gameId) {
        const target = `/game/${data.gameId}`;
        // Give the component-level handler (FreePlayWaitingRoom) a chance to navigate
        // first; if it already did, the path will be /game/... and we skip the reload.
        setTimeout(() => {
          if (!window.location.pathname.startsWith('/game/')) {
            console.log('🌐 Global seating-phase-started fallback → navigating to', target);
            window.location.href = target;
          }
        }, 1500);
      }
    });

    setSocket(newSocket);
  }, [currentUser, getToken]);

  useEffect(() => {
    if (currentUser) {
      if (connectionTimeout.current) clearTimeout(connectionTimeout.current);
      connectionTimeout.current = setTimeout(() => initializeSocket(), 500);
    } else {
      if (socket) { socket.disconnect(); setSocket(null); }
      setIsConnected(false);
      setConnectionStatus('disconnected');
      setConnectionError(null);
      setCurrentRoom({ roomCode: null, gameId: null, seatPosition: null });
    }
    return () => { if (connectionTimeout.current) clearTimeout(connectionTimeout.current); };
  }, [currentUser, initializeSocket]);

  useEffect(() => {
    return () => {
      if (socket) socket.disconnect();
      if (connectionTimeout.current) clearTimeout(connectionTimeout.current);
    };
  }, []);

  const manualReconnect = useCallback(async () => {
    if (!currentUser) return false;
    setConnectionStatus('reconnecting');
    setConnectionError(null);
    initializeSocket();
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (isConnected && connectionStatus === 'connected') {
          clearInterval(checkInterval);
          resolve(true);
        }
      }, 100);
      setTimeout(() => { clearInterval(checkInterval); resolve(false); }, 10000);
    });
  }, [currentUser, initializeSocket, isConnected, connectionStatus]);

  const checkConnection = useCallback(() => socket?.connected || false, [socket]);

    const emitWithPromise = useCallback((event, data = {}, timeout = 10000) => {
    return new Promise((resolve, reject) => {
      if (!socket || !socket.connected) {
        reject(new Error('Socket not connected'));
        return;
      }
      const timeoutId = setTimeout(() => reject(new Error(`Request timeout for event: ${event}`)), timeout);
            socket.emit(event, data, (response) => {
        clearTimeout(timeoutId);
        if (response && response.success === false) {
          const err = new Error(response.error || `Request failed for event: ${event}`);
          err.response = response; // Attach full response so callers can extract data (e.g. pointTable)
          reject(err);
        } else if (response && response.error) {
          const err = new Error(response.error);
          err.response = response;
          reject(err);
        } else resolve(response || {});
      });
    });
  }, [socket]);

  const emitEvent = useCallback((event, data = {}) => {
    if (!socket || !socket.connected) { return false; }
    socket.emit(event, data);
    return true;
  }, [socket]);

  // Fetch active freeplay + cash rooms for the lobby dropdown.
  const getActiveRooms = useCallback(async () => {
    if (!socket || !socket.connected) return { freeplay: [], cash: [] };
    const res = await emitWithPromise('get-active-rooms', {}, 10000);
    return { freeplay: res?.freeplay || [], cash: res?.cash || [] };
  }, [socket, emitWithPromise]);

    // ==================== FREE PLAY METHODS ====================
  const joinFreePlay = useCallback(async () => {
    return await joinFreePlayWithOptions({});
  }, [currentUser, socket]);

  const joinFreePlayWithOptions = useCallback(async (options = {}) => {
    if (!currentUser) throw new Error('Please login to join free play');
    if (!socket || !socket.connected) throw new Error('Socket not connected');
    return new Promise((resolve, reject) => {
      let timeoutId = setTimeout(() => reject(new Error('Timeout waiting for room (15s)')), 15000);
      const roomJoinedHandler = (data) => {
        clearTimeout(timeoutId);
        socket.off('room-joined', roomJoinedHandler);
        socket.off('error', errorHandler);
        if (data && data.success && data.roomCode) {
          setCurrentRoom({ roomCode: data.roomCode, gameId: data.gameId, seatPosition: data.seatPosition });
          resolve(data);
        } else reject(new Error(data?.message || 'Failed to join room'));
      };
      const errorHandler = (errorData) => {
        clearTimeout(timeoutId);
        socket.off('room-joined', roomJoinedHandler);
        socket.off('error', errorHandler);
        reject(new Error(errorData?.message || 'Failed to join free play'));
      };
      socket.on('room-joined', roomJoinedHandler);
      socket.on('error', errorHandler);
      socket.emit('join-free-play', options);
    });
  }, [currentUser, socket]);

  const leaveRoom = useCallback(async (roomCode) => {
    if (!roomCode) throw new Error('Room code is required');
    const response = await emitWithPromise('leave-room', { roomCode }, 8000);
    if (response && response.success) setCurrentRoom({ roomCode: null, gameId: null, seatPosition: null });
    return response || {};
  }, [emitWithPromise]);

  const setPlayerReady = useCallback(async (gameId, isReady = true) => {
    if (!gameId) throw new Error('Game ID is required');
    return await emitWithPromise('player-ready', { gameId, isReady }, 8000);
  }, [emitWithPromise]);

  // ==================== GAME PLAY METHODS ====================
  const drawCard = useCallback(async (gameId, fromDeck = true, stateHash = null) => {
    if (!gameId) throw new Error('Game ID is required');
    if (!currentUser) throw new Error('User not authenticated');
    const payload = { gameId, userId: currentUser.id, fromDeck };
    const finalStateHash = stateHash || gameStateRef.current?.stateHash;
    if (finalStateHash) payload.stateHash = finalStateHash;
    return await emitWithPromise('draw-card', payload, 10000);
  }, [currentUser, emitWithPromise]);

  const discardCard = useCallback(async (gameId, cardId, stateHash = null) => {
    if (!gameId || !cardId) throw new Error('Game ID and Card ID are required');
    if (!currentUser) throw new Error('User not authenticated');
    const payload = { gameId, userId: currentUser.id, cardId };
    const finalStateHash = stateHash || gameStateRef.current?.stateHash;
    if (finalStateHash) payload.stateHash = finalStateHash;
    return await emitWithPromise('discard-card', payload, 10000);
  }, [currentUser, emitWithPromise]);










        const declareRummy = useCallback(async (gameId, stateHash = null, sets = [], runs = [], discardedCardId = null) => {
    if (!gameId) throw new Error('Game ID is required');
    if (!currentUser) throw new Error('User not authenticated');
    const payload = { gameId, userId: currentUser.id, sets, runs, discardedCardId };
    const finalStateHash = stateHash || gameStateRef.current?.stateHash;
    if (finalStateHash) payload.stateHash = finalStateHash;
    return await emitWithPromise('declare-rummy', payload, 10000);
  }, [currentUser, emitWithPromise]);

  // ==================== ADDITIONAL GAME METHODS ====================
  const formMeld = useCallback(async (gameId, cards, meldType = 'group', stateHash = null) => {
    if (!gameId || !cards) throw new Error('Game ID and cards are required');
    if (!currentUser) throw new Error('User not authenticated');
    const payload = { gameId, userId: currentUser.id, cards, meldType };
    const finalStateHash = stateHash || gameStateRef.current?.stateHash;
    if (finalStateHash) payload.stateHash = finalStateHash;
    return await emitWithPromise('form-meld', payload, 10000);
  }, [currentUser, emitWithPromise]);

  const startNextDeal = useCallback(async (gameId) => {
    if (!gameId) throw new Error('Game ID is required');
    return await emitWithPromise('start-next-deal', { gameId }, 10000);
  }, [emitWithPromise]);

  const autoArrange = useCallback(async (gameId, stateHash = null) => {
    if (!gameId) throw new Error('Game ID is required');
    const payload = { gameId };
    const finalStateHash = stateHash || gameStateRef.current?.stateHash;
    if (finalStateHash) payload.stateHash = finalStateHash;
    return await emitWithPromise('auto-arrange', payload, 10000);
  }, [emitWithPromise]);

  const dropFromGame = useCallback(async (gameId, stateHash = null) => {
    if (!gameId) throw new Error('Game ID is required');
    const payload = { gameId };
    const finalStateHash = stateHash || gameStateRef.current?.stateHash;
    if (finalStateHash) payload.stateHash = finalStateHash;
    return await emitWithPromise('drop-from-game', payload, 10000);
  }, [emitWithPromise]);

  const reconnectToGame = useCallback(async (gameId) => {
    if (!gameId) throw new Error('Game ID is required');
    return await emitWithPromise('reconnect-to-game', { gameId }, 10000);
  }, [emitWithPromise]);

  const leaveGame = useCallback(async (gameId) => {
    if (!gameId) throw new Error('Game ID is required');
    return await emitWithPromise('leave-free-play', { gameId }, 8000);
  }, [emitWithPromise]);

  // ==================== FREE PLAY STATUS METHODS ====================
  const quickJoinFreePlay = useCallback(async () => {
    return await joinFreePlay();
  }, [joinFreePlay]);

  const getFreePlayStatus = useCallback(async (gameId) => {
    if (!gameId) throw new Error('Game ID is required');
    return await emitWithPromise('request-room-sync', { gameId }, 10000);
  }, [emitWithPromise]);

  const leaveFreePlay = useCallback(async (gameId) => {
    if (!gameId) throw new Error('Game ID is required');
    return await leaveGame(gameId);
  }, [leaveGame]);

  const setFreePlayReady = useCallback(async (gameId, isReady = true) => {
    return await setPlayerReady(gameId, isReady);
  }, [setPlayerReady]);

    const startFreePlayGame = useCallback(async (gameId) => {
    // The server auto-starts free play games when ready; this is a no-op
    return { success: true, message: 'Game will start automatically' };
  }, []);

  // ==================== CASH RUMMY METHODS ====================
  const joinCashRummy = useCallback(async (options = {}) => {
    if (!currentUser) throw new Error('Please login to play cash games');
    if (!socket || !socket.connected) throw new Error('Socket not connected');
    
    return new Promise((resolve, reject) => {
      let timeoutId = setTimeout(() => reject(new Error('Timeout waiting for room (15s)')), 15000);
      
      const roomJoinedHandler = (data) => {
        // Only the reply to THIS join counts. Room-sync replies (sent for a table that is already
        // open, possibly a finished game) have no isNewJoin flag and must not hijack a new join.
        if (!data || data.isNewJoin !== true) return;
        clearTimeout(timeoutId);
        socket.off('cash-room-joined', roomJoinedHandler);
        socket.off('error', errorHandler);
        
        if (data && data.success && data.roomCode) {
          setCurrentRoom({ roomCode: data.roomCode, gameId: data.gameId, seatPosition: data.seatPosition });
          resolve(data);
        } else {
          reject(new Error(data?.message || 'Failed to join cash room'));
        }
      };
      
      const errorHandler = (errorData) => {
        clearTimeout(timeoutId);
        socket.off('cash-room-joined', roomJoinedHandler);
        socket.off('error', errorHandler);
        reject(new Error(errorData?.message || 'Failed to join cash game'));
      };
      
      socket.on('cash-room-joined', roomJoinedHandler);
      socket.on('error', errorHandler);
      socket.emit('join-cash-rummy', options);
    });
  }, [currentUser, socket]);

  const leaveCashRummy = useCallback(async (gameId) => {
    if (!gameId) throw new Error('Game ID is required');
    return await emitWithPromise('leave-cash-rummy', { gameId }, 8000);
  }, [emitWithPromise]);

  // ==================== PRACTICE (VS AI) METHOD ====================
  const joinPractice = useCallback(async (options = {}) => {
    if (!currentUser) throw new Error('Please login to play practice games');
    if (!socket || !socket.connected) throw new Error('Socket not connected');
    return await emitWithPromise('join-practice', options, 15000);
  }, [currentUser, socket, emitWithPromise]);

    const value = {
    socket,
    isConnected,
    connectionStatus,
    connectionError,
    currentRoom,
    reconnect: manualReconnect,
    checkConnection,
    emitEvent,
    emitWithPromise,
    updateGameStateRef,
        joinFreePlay,
    joinFreePlayWithOptions,
    // Cash Rummy methods
    joinCashRummy,
    leaveCashRummy,
    // Practice vs AI
    joinPractice,
    leaveRoom,
    setPlayerReady,
    drawCard,
    discardCard,
    declareRummy,
    // Additional game methods
    formMeld,
    startNextDeal,
    autoArrange,
    dropFromGame,
    reconnectToGame,
    leaveGame,
    // Free play methods
    quickJoinFreePlay,
    getFreePlayStatus,
    leaveFreePlay,
    setFreePlayReady,
    startFreePlayGame,
    getActiveRooms,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketContext;